import { Html5Qrcode } from 'html5-qrcode'
import { useCallback, useEffect, useRef } from 'react'

interface QrScannerProps {
  onScanSuccess: (decodedText: string) => void
  onScanError?: (error: string) => void
  cameraFacing: 'user' | 'environment'
  paused?: boolean
}

const SCANNER_ELEMENT_ID = 'qr-scanner-region'

export const QrScanner: React.FC<QrScannerProps> = ({
  onScanSuccess,
  onScanError,
  cameraFacing,
  paused = false,
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const isRunningRef = useRef(false)

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isRunningRef.current) {
      try {
        await scannerRef.current.stop()
        isRunningRef.current = false
      } catch {
        // ignore stop errors
      }
    }
  }, [])

  const startScanner = useCallback(async () => {
    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID)
    }

    if (isRunningRef.current) {
      await stopScanner()
    }

    try {
      await scannerRef.current.start(
        { facingMode: cameraFacing },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
          disableFlip: false,
        },
        (decodedText) => {
          onScanSuccess(decodedText)
        },
        () => {
          // scan error (no QR found in frame) — silent
        },
      )
      isRunningRef.current = true
    } catch (err) {
      onScanError?.(
        err instanceof Error ? err.message : 'Failed to start camera',
      )
    }
  }, [cameraFacing, onScanSuccess, onScanError, stopScanner])

  useEffect(() => {
    if (!paused) {
      startScanner()
    } else {
      stopScanner()
    }

    return () => {
      stopScanner()
    }
  }, [paused, startScanner, stopScanner])

  // Restart scanner when camera facing changes
  useEffect(() => {
    if (!paused && isRunningRef.current) {
      stopScanner().then(() => startScanner())
    }
  }, [cameraFacing])

  return (
    <div className="w-full h-full overflow-hidden">
      <div
        id={SCANNER_ELEMENT_ID}
        className="!absolute inset-0 [&_video]:!object-cover [&_video]:!w-full [&_video]:!h-full"
      />
    </div>
  )
}
